import { parseOmnisearch, InputValues } from "components/Omnisearch/FilterPopover";
import { useMemo } from "react";

export type ParsedOmnisearchResult = {
  /** Raw parsed object from parseOmnisearch */
  parsed: InputValues;
  /** Get a single value by key */
  getValue: (key: string) => string | string[] | undefined;
  /** Check if a key exists in the parsed result */
  hasKey: (key: string) => boolean;
  /** All keys present in the parsed result */
  keys: string[];
};

/**
 * Memoized hook for parsing omnisearch strings.
 *
 * This hook centralizes omnisearch parsing to avoid redundant parsing
 * operations across components. The result is memoized and only
 * recomputed when the omnisearch string or keyAliases change.
 *
 * @param omnisearch - The omnisearch string to parse
 * @param keyAliases - Optional key aliases (e.g., { tags: "facilityTags" })
 * @returns Parsed result with helper methods
 *
 * @example
 * const { parsed, getValue, hasKey } = useParsedOmnisearch(omnisearch, keyAliases);
 *
 * // Get a specific filter value
 * const status = getValue('status'); // string | string[] | undefined
 *
 * // Check if a filter is applied
 * if (hasKey('state')) {
 *   // handle state filter
 * }
 */
export function useParsedOmnisearch(
  omnisearch: string,
  keyAliases?: Record<string, string>
): ParsedOmnisearchResult {
  return useMemo(() => {
    const parsed = parseOmnisearch(omnisearch, keyAliases);

    return {
      parsed,
      getValue: (key: string): string | string[] | undefined => parsed[key],
      hasKey: (key: string): boolean => key in parsed,
      keys: Object.keys(parsed),
    };
  }, [omnisearch, keyAliases]);
}
